import {
  IndexedInteractionStepResponse,
  MOCK_INTERACTION_MULTI_TURNS_PARAMS,
  ChatTaskContextType,
} from '@scale/shared/types/chat';
import { TEST_CHAT_SUBTASK, TEST_CHAT_PROJECT, ReviewLevel } from '@scale/shared/defaults';
import { ChatStoreContextProvider, useCreateChatStore } from '../../Chat/hooks/ChatStore';
import { useStore } from 'zustand';
import { FC, PropsWithChildren } from 'react';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';
import { ChatTaskContext } from '../contexts/ChatTaskContext';
import { useCommonChatTaskContextConfigs } from 'react-scale/components/scalers/tasks/Chat/hooks/configs';

type DemoProvidersProps = PropsWithChildren<{
  chatTaskFeatureConfig: IChatTaskFeatureConfig;
  restrictRollbacks: boolean;
}>;

export const DemoProviders: FC<DemoProvidersProps> = ({
  children,
  chatTaskFeatureConfig,
  restrictRollbacks,
}) => {
  let defaultConfig = MOCK_INTERACTION_MULTI_TURNS_PARAMS;
  let defaultResponses: IndexedInteractionStepResponse[] = [];
  const savedStr = localStorage.getItem('multi-turn-save-state');
  if (savedStr) {
    const saved = JSON.parse(savedStr);
    defaultConfig = saved.config;
    defaultResponses = saved.responses;
  }
  const chatStore = useCreateChatStore(
    {
      config: defaultConfig,
      responses: defaultResponses,
      reviewLevel: ReviewLevel.Attempt,
    },
    undefined,
    'ThreadedInteractionDemo - ChatStore',
  );

  const currentConfig = useStore(chatStore.current, state => state.config);
  const reviewLevel = useStore(chatStore.current, state => state.reviewLevel);

  const canEditComments = reviewLevel !== ReviewLevel.Attempt;

  const commonChatTaskContextConfigs = useCommonChatTaskContextConfigs(
    currentConfig,
    reviewLevel,
    chatTaskFeatureConfig,
    currentConfig.templateVariables,
  );

  const context: ChatTaskContextType = {
    assignmentId: TEST_CHAT_SUBTASK,
    projectId: TEST_CHAT_PROJECT,
    ...commonChatTaskContextConfigs,
    // The demo component specifically has a UI feature to restrict rollbacks
    restrictRollbacks:
      restrictRollbacks ||
      reviewLevel === ReviewLevel.ReviewConsensus ||
      (currentConfig.restrictRollbacksInReview && reviewLevel !== ReviewLevel.Attempt),
    canEditComments,
    isPipelineV2: true, // disable autosave
    disableLinter: true,
  };
  return (
    <ChatStoreContextProvider store={chatStore}>
      <ChatTaskContext.Provider value={context}>{children}</ChatTaskContext.Provider>
    </ChatStoreContextProvider>
  );
};
